import type { Type, TypeQueryOptions } from '@/types';
import { useQuery } from 'react-query';
import { DEFAULT_APP_LOCALE } from '@/lib/constants';
import client from './client';
import { API_ENDPOINTS } from './client/api-endpoints';

export function useTypes(options?: Partial<TypeQueryOptions>) {
  // SSR prefetches types with `language`; the key must match to reuse the dehydrated data.
  const queryOptions = { language: DEFAULT_APP_LOCALE, ...options };
  const { data, isLoading, error } = useQuery<Type[], Error>(
    [API_ENDPOINTS.TYPES, queryOptions],
    ({ queryKey }) => client.types.all(Object.assign({}, queryKey[1])),
    {
      staleTime: 12 * 60 * 60 * 1000, // 12 hours
    },
  );
  return {
    types: data,
    isLoading,
    error,
  };
}

export function useType(slug: string) {
  const { data, isLoading, error } = useQuery<Type, Error>(
    [API_ENDPOINTS.TYPES, { slug }],
    () => client.types.get({ slug }),
    {
      enabled: Boolean(slug),
      // Matches useTypes (which already caches for 12h). Prevents duplicate
      // /types/{slug} requests from the multiple components on the home page
      // that call useType(variables.type) for the same slug (e.g. promotions,
      // banner, layout).
      staleTime: 12 * 60 * 60 * 1000,
    },
  );
  return {
    type: data,
    isLoading,
    error,
  };
}
